import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import colors from 'styles/colors';
import CheckCircle from '@mui/icons-material/CheckCircle';

const ICON_SIZE = 24;
const CIRCLE_RADIUS = 9;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;
const CENTER = ICON_SIZE / 2;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${ICON_SIZE}px;
  height: ${ICON_SIZE}px;
  flex-shrink: 0;
`;

export interface StepToastCountdownProps {
  /** Total countdown duration in milliseconds */
  duration: number;
  /** Called when the countdown reaches zero */
  onComplete?: () => void;
  /** Force a specific progress for Storybook (0 = full, 1 = empty) */
  forceProgress?: number;
}

/**
 * Circular countdown indicator for undo toast actions.
 * Renders as a filled circle arc that depletes linearly over the given
 * duration. Shows a green check when complete.
 */
export const StepToastCountdown = ({
  duration,
  onComplete,
  forceProgress,
}: StepToastCountdownProps) => {
  const [progress, setProgress] = useState(forceProgress ?? 0);
  const [complete, setComplete] = useState(forceProgress === 1);
  const rafRef = useRef<number>(0);
  const startRef = useRef(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (forceProgress != null) return;

    // Reset on (re)start so a reused instance with a new duration doesn't stay
    // stuck in a previously-completed state.
    setProgress(0);
    setComplete(false);

    // Non-positive duration: nothing to count down — complete immediately.
    if (duration <= 0) {
      setProgress(1);
      setComplete(true);
      onCompleteRef.current?.();
      return;
    }

    startRef.current = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startRef.current;
      const p = Math.min(elapsed / duration, 1);
      setProgress(p);

      if (p >= 1) {
        setComplete(true);
        onCompleteRef.current?.();
      } else {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [duration, forceProgress]);

  if (complete) {
    return (
      <Wrapper data-testid="step-toast-countdown">
        <CheckCircle sx={{ fontSize: ICON_SIZE, color: colors.green6 }} />
      </Wrapper>
    );
  }

  // SVG arc: strokeDashoffset controls how much of the circle is drawn.
  // Negative offset depletes clockwise; rotate(-90deg) starts from 12 o'clock.
  // progress 0 = full circle (offset 0), progress 1 = empty (offset = -circumference).
  const offset = -(progress * CIRCLE_CIRCUMFERENCE);

  return (
    <Wrapper data-testid="step-toast-countdown">
      <svg
        width={ICON_SIZE}
        height={ICON_SIZE}
        viewBox={`0 0 ${ICON_SIZE} ${ICON_SIZE}`}
        style={{ transform: 'rotate(-90deg)' }}
      >
        {/* Background track */}
        <circle
          cx={CENTER}
          cy={CENTER}
          r={CIRCLE_RADIUS}
          fill="none"
          stroke={colors.gray7}
          strokeWidth={2}
        />
        {/* Depleting arc */}
        <circle
          cx={CENTER}
          cy={CENTER}
          r={CIRCLE_RADIUS}
          fill="none"
          stroke={colors.green6}
          strokeWidth={2}
          strokeDasharray={CIRCLE_CIRCUMFERENCE}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
    </Wrapper>
  );
};
